/**
 * Hinweisbox fuer Inhalte, die ohne Cookie-Zustimmung blockiert sind.
 *
 * DCCookie rendert diese Box nur fuer Provider, deren Kategorie in
 * externalMediaCategories steht (CookieRenderHelper.renderAllExternalMediaFrames
 * ueber CookieHelper.getAllDeclinedExternalMediaProviders). HubSpot liegt bei
 * uns in der Kategorie "marketing" und faellt damit aus dem Filter - die
 * Container blieben ohne Zustimmung komplett leer, waehrend OpenStreetMap
 * (Kategorie external_media) den Hinweis bekommt.
 *
 * Wir bauen die Box deshalb selbst, mit den Klassen und Texten von DCCookie,
 * damit sie genauso aussieht und sich genauso verhaelt wie der Hinweis auf der
 * Kontaktseite. Der Klick-Handler fuer
 * .dcCookieHelper--buttonExternalSpecificAccept haengt document-weit und wird in
 * DCCookie.init() immer registriert - unabhaengig von Kategorie und
 * externalMediaInfoButtonType. Unser Button loest darueber
 * CookieHelper.toggleCookie(providerId, true) aus, DCCookie uebernimmt danach
 * Consent-Speicherung, Script-Freigabe und iframe-Ersetzung.
 */
import {hasCookieConsent, onCookieConsent} from "./cookieConsent";

/** Pro Provider nur ein Consent-Observer, egal wie viele Boxen gerendert sind. */
const consentWatchers = new Map();

function placeholderSelector(providerId) {
    return `[data-cookie-placeholder="${providerId}"]`;
}

/**
 * Texte kommen aus der DCCookie-Konfiguration, damit DE und EN sowie spaetere
 * CMS-Aenderungen ohne Anpassung hier funktionieren.
 */
function getCookieTexts(providerId) {
    const globals = window.GLOBALS ?? {};
    const localization = globals.dcCookie?.localizationData;
    const general = localization?.general;
    const provider = localization?.provider?.[providerId];

    if (!general || !provider) return null;

    const providerName = provider.name || providerId;
    const fill = text => (text || '').replace(/\{\{provider\.name\}\}/g, providerName);

    return {
        headline: fill(general.external_info_headline),
        text: fill(general.external_info_text),
        moreInfo: globals.tc?.cookieMoreInformation,
        accept: fill(globals.tc?.cookieExternalSpecificAccept) || providerName
    };
}

function createButton(className, label) {
    const button = document.createElement('button');
    button.type = 'button';
    // Kein Icon, kein inneres Element: DCCookie prueft im document-Handler
    // event.target.classList - ein Kindelement wuerde den Klick verschlucken.
    button.className = className;
    button.textContent = label;
    return button;
}

function buildPlaceholder(providerId, texts) {
    const box = document.createElement('div');
    box.className = 'dcCookieExternal__info';
    box.dataset.cookiePlaceholder = providerId;

    const headline = document.createElement('div');
    headline.className = 'dcCookieExternal__headline';
    headline.textContent = texts.headline;
    box.appendChild(headline);

    const text = document.createElement('div');
    text.className = 'dcCookieExternal__text';
    text.textContent = texts.text;
    box.appendChild(text);

    const buttonRow = document.createElement('div');
    buttonRow.className = 'buttonRowBottom';

    if (texts.moreInfo) {
        buttonRow.appendChild(createButton(
            'btnNoStyle button dcCookieHelper--buttonExternalMoreInfo',
            texts.moreInfo
        ));
    }

    const accept = createButton(
        'btnNoStyle button button--primary dcCookieHelper--buttonExternalSpecificAccept',
        texts.accept
    );
    accept.dataset.providerid = providerId;
    accept.addEventListener('click', () => {
        // Der DCCookie-Handler an document laeuft direkt im Anschluss an diesen
        // hier, erst danach steht der Consent. Deshalb im naechsten Tick
        // pruefen - laedt DCCookie gerade noch (dccookie-loader.js wartet 250ms),
        // bleibt die Box stehen statt kommentarlos zu verschwinden.
        setTimeout(() => {
            if (hasCookieConsent(providerId)) removeCookiePlaceholders(providerId);
        }, 0);
    });
    buttonRow.appendChild(accept);

    box.appendChild(buttonRow);
    return box;
}

/**
 * Raeumt die Boxen weg, sobald der Provider freigegeben ist - egal ob ueber
 * unseren Button oder ueber das Cookie-Banner.
 */
function watchConsent(providerId) {
    if (consentWatchers.has(providerId)) return;

    consentWatchers.set(providerId, onCookieConsent(providerId, document.body, () => {
        consentWatchers.delete(providerId);
        removeCookiePlaceholders(providerId);
    }));
}

export function removeCookiePlaceholders(providerId) {
    document.querySelectorAll(placeholderSelector(providerId)).forEach(box => box.remove());
}

/**
 * @param {HTMLElement} target - Container des blockierten Inhalts
 * @param {string} providerId - provider_id aus dem CMS, z.B. "hubspot"
 * @param {{position?: 'inside'|'before'}} [options]
 *        inside: DCCookie leert den Container bei Zustimmung selbst.
 *        before: fuer Container mit iframeclass - DCCookie ersetzt den DIV dort
 *        durch ein iframe und wuerde den Inhalt mitnehmen.
 * @returns {HTMLElement|null} die gerenderte Box
 */
export function renderCookiePlaceholder(target, providerId, options = {}) {
    if (!target || hasCookieConsent(providerId)) return null;

    const position = options.position ?? 'inside';
    const alreadyRendered = position === 'before'
        ? target.previousElementSibling?.dataset?.cookiePlaceholder === providerId
        : !!target.querySelector(placeholderSelector(providerId));

    if (alreadyRendered) return null;

    const texts = getCookieTexts(providerId);
    // Ohne konfigurierte Texte lieber nichts rendern als eine leere Box.
    if (!texts?.headline || !texts?.text) return null;

    const box = buildPlaceholder(providerId, texts);

    if (position === 'before') {
        target.parentNode?.insertBefore(box, target);
    } else {
        target.appendChild(box);
    }

    watchConsent(providerId);
    return box;
}

